import React from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { FreeTierUsageSummary } from '../../services/api';

type FeatureType = 'practice' | 'survival' | 'rush';

interface FreeTierLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: FeatureType;
  usage?: FreeTierUsageSummary | null;
}

const featureCopy: Record<FeatureType, { title: string; description: string; highlight: string }> = {
  practice: {
    title: 'Daily Practice Limit Reached',
    description: 'Free members can attempt up to 5 practice questions per day. Upgrade to unlock unlimited practice mode access.',
    highlight: 'Unlimited practice sessions and full access to all study tools with Cracked At Pro.',
  },
  survival: {
    title: 'Survival Run Used',
    description: 'Free members can start one Survival challenge per day. Upgrade to chase new personal bests without limits.',
    highlight: 'Unlimited Survival runs, advanced stats, and exclusive leaderboards await in Cracked At Pro.',
  },
  rush: {
    title: 'Rush Run Used',
    description: 'Free members can start one Rush challenge per day. Upgrade to keep the streak going.',
    highlight: 'Unlock unlimited Rush sessions plus every premium feature across the platform.',
  },
};

const formatResetLabel = (usage?: FreeTierUsageSummary | null) => {
  if (!usage) return null;
  try {
    const resetDate = new Date(usage.resets_at);
    return `Resets ${resetDate.toLocaleString(undefined, {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  } catch {
    return `Resets ${usage.resets_on}`;
  }
};

const FreeTierLimitModal: React.FC<FreeTierLimitModalProps> = ({
  isOpen,
  onClose,
  feature,
  usage,
}) => {
  if (!isOpen) {
    return null;
  }

  const copy = featureCopy[feature];

  const usageSummary = (() => {
    if (!usage) return null;
    if (feature === 'practice') {
      return `${usage.practice_questions_used}/${usage.practice_questions_limit} practice questions used today`;
    }
    if (feature === 'survival') {
      return `${usage.survival_sessions_used}/${usage.survival_sessions_limit} Survival run used today`;
    }
    return `${usage.rush_sessions_used}/${usage.rush_sessions_limit} Rush run used today`;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-mango-500 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="mb-4 flex items-center space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-mango-100 text-mango-600 dark:bg-mango-900">
            <SparklesIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{copy.title}</h2>
            {usageSummary && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{usageSummary}</p>
            )}
          </div>
        </div>

        <p className="text-gray-700 dark:text-gray-300">{copy.description}</p>

        <div className="mt-4 rounded-lg bg-mango-50 p-4 text-sm text-mango-800 dark:bg-mango-900/40 dark:text-mango-200">
          {copy.highlight}
        </div>

        {usage && (
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {formatResetLabel(usage)}
          </div>
        )}

        <div className="mt-6 flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
          <Link
            to="/subscription"
            className="inline-flex w-full items-center justify-center rounded-lg bg-mango-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-mango-500 focus:outline-none focus:ring-2 focus:ring-mango-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            View Subscription Plans
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-mango-500 focus:ring-offset-2 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:ring-offset-gray-900"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeTierLimitModal;
